// src/pages/AgentProperties.jsx - COMPLETE FIXED VERSION WITH DYNAMIC UPDATES

import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useProperty } from "./PropertyContext";
import AddPropertyPopup from "./AddPropertyPopup";
import AddUpcomingProjectPopup from "./AddUpcomingProjectPopup";
import DeletePropertyModal from "../../components/DeletePropertyModal/DeletePropertyModal";
import { API_BASE_URL } from "../../config/api.config";
import { authAPI, sellerDashboardAPI, sellerProfileAPI, sellerPropertiesAPI } from "../../services/api.service";
import "../../Seller-Owner/styles/SellerProperties.css";
import "../styles/AgentPropertiesOverrides.css";
import "../styles/AgentPropertiesLayout.css";
import "../styles/AgentProperties.css";

const FREE_LISTING_SLOTS = 1;
const FREE_PLAN_LABEL = 'Free (1 month)';

const getFreeListingPropertyId = (props) => {
  if (!props?.length) return null;
  const sorted = [...props].sort(
    (a, b) => new Date(a.createdAt || a.created_at) - new Date(b.createdAt || b.created_at)
  );
  return sorted[0]?.id ?? null;
};

const formatDaysLeftLabel = (daysLeft, isExpired = false) => {
  if (isExpired || daysLeft <= 0) return 'Expired';
  return `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`;
};

const PROJECT_STATUS_OPTIONS = [
  { value: "UNDER CONSTRUCTION", label: "Under Construction" },
  { value: "PRE-LAUNCH", label: "Pre-Launch" },
  { value: "COMPLETED", label: "Completed" }
];

const MAX_PROPERTIES = 25;

const AgentProperties = () => {
  const navigate = useNavigate();
  const { properties, deleteProperty, loading, error, refreshData, setProperties } = useProperty();
  const [showForm, setShowForm] = useState(false);
  const [showUpcomingProjectPopup, setShowUpcomingProjectPopup] = useState(false);
  const [editIndex, setEditIndex] = useState(null);
  const [editProperty, setEditProperty] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [searchTerm, setSearchTerm] = useState('');
  const [deletingId, setDeletingId] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [propertyToDelete, setPropertyToDelete] = useState(null);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);
  // const [planInfo, setPlanInfo] = useState({ total_slots: 0, active_properties: 0, remaining_slots: 0, nearest_expiry: null, subscriptions: [] });
  const [planInfo, setPlanInfo] = useState({ 
  total_slots: 0, 
  active_properties: 0, 
  remaining_slots: 0, 
  nearest_expiry: null, 
  subscriptions: [],
  has_free_trial: false 
});
  const [isPlanLoading, setIsPlanLoading] = useState(true);
  const [visibilityModalOpen, setVisibilityModalOpen] = useState(false);
  const [propertyToToggle, setPropertyToToggle] = useState(null);
  const [togglingPropertyId, setTogglingPropertyId] = useState(null);
  
  const currentUser = authAPI.getUser();
  const [userProfile, setUserProfile] = useState(null);
  
  // Store which subscription belongs to which property (IDs only, not dates)
  const [propertySubscriptionIdMap, setPropertySubscriptionIdMap] = useState(() => {
    const saved = localStorage.getItem('agentPropertySubscriptionIdMap');
    return saved ? JSON.parse(saved) : {};
  });

  // Fetch user profile on mount
  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        const response = await sellerProfileAPI.get();
        if (response.success && response.data?.profile) {
          setUserProfile(response.data.profile);
        }
      } catch (err) {
        console.error('Failed to fetch user profile:', err);
      }
    };
    fetchUserProfile();
  }, []);

  // Calculate free trial based on account creation date
  const getHasFreeTrial = useCallback(() => {
    const userData = userProfile || currentUser;
    const createdAtRaw = userData?.created_at || userData?.createdAt;
    
    if (!createdAtRaw) return false;
    
    const createdAt = new Date(createdAtRaw);
    const trialEndsAt = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
    return new Date() < trialEndsAt;
  }, [userProfile, currentUser]);

  // Fetch subscriptions from backend
  const fetchSubscriptions = useCallback(async () => {
    try {
      const result = await sellerDashboardAPI.getSubscriptions();
      if (result.success && result.data?.subscriptions) {
        return result.data.subscriptions.filter(sub => sub.status === 'active');
      }
    } catch (err) {
      console.error('Error fetching subscriptions:', err);
    }
    return [];
  }, []);

  // Initialize/update property-subscription mapping
  useEffect(() => {
    const initMapping = async () => {
      if (properties.length === 0) return;
      
      const subscriptions = await fetchSubscriptions();
      const hasFreeTrial = getHasFreeTrial();
      
      // Determine which properties need paid subscriptions (exclude free listing slot)
      const freeListingPropertyId = hasFreeTrial ? getFreeListingPropertyId(properties) : null;
      const propertiesNeedingPaid = freeListingPropertyId
        ? properties.filter((p) => p.id !== freeListingPropertyId)
        : properties;
      const newMappings = { ...propertySubscriptionIdMap };
      let changed = false;
      
      propertiesNeedingPaid.forEach((property, index) => {
        // If property doesn't have a subscription assigned, assign one
        if (!newMappings[property.id] && subscriptions[index]) {
          newMappings[property.id] = subscriptions[index].id;
          changed = true;
          console.log(`Assigned subscription ${subscriptions[index].id} to property ${property.id}`);
        }
      });
      
      if (changed) {
        setPropertySubscriptionIdMap(newMappings);
        localStorage.setItem('agentPropertySubscriptionIdMap', JSON.stringify(newMappings));
      }
    };
    
    initMapping();
  }, [properties, fetchSubscriptions, getHasFreeTrial]);

// Fetch plan info with subscriptions
useEffect(() => {
  const fetchPlanInfo = async () => {
    setIsPlanLoading(true);
    try {
      const [statsResult, subscriptionsResult] = await Promise.all([
        sellerDashboardAPI.getStats(),
        sellerDashboardAPI.getSubscriptions(),
      ]);

      let subscriptions = [];
      
      if (subscriptionsResult?.success && subscriptionsResult.data?.subscriptions) {
        subscriptions = subscriptionsResult.data.subscriptions.filter(
          sub => sub.status === 'active'
        );
      }
      
      // Calculate free trial based on user profile
      const userData = userProfile || currentUser;
      const createdAtRaw = userData?.created_at || userData?.createdAt;
      let hasFreeTrial = false;
      
      if (createdAtRaw) {
        const createdAt = new Date(createdAtRaw);
        const trialEndsAt = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
        hasFreeTrial = new Date() < trialEndsAt;
      }
      
      const totalSlots = subscriptions.length + (hasFreeTrial ? FREE_LISTING_SLOTS : 0);
      
      console.log('=== PLAN INFO CALCULATION ===');
      console.log('Subscriptions count:', subscriptions.length);
      console.log('Has free trial:', hasFreeTrial);
      console.log('Total slots:', totalSlots);
      console.log('Properties count:', properties.length);
      
      setPlanInfo({
        total_slots: totalSlots,
        active_properties: properties.length,
        remaining_slots: Math.max(0, totalSlots - properties.length),
        nearest_expiry: subscriptions[0]?.ends_at || null,
        subscriptions: subscriptions,
        has_free_trial: hasFreeTrial
      });
    } catch (err) {
      console.error('Failed to fetch subscription slot info:', err);
      setPlanInfo({ 
        total_slots: 0, 
        active_properties: 0, 
        remaining_slots: 0, 
        nearest_expiry: null,
        subscriptions: [],
        has_free_trial: false
      });
    } finally {
      setIsPlanLoading(false);
    }
  };

  fetchPlanInfo();
}, [properties, userProfile]); // Remove getHasFreeTrial from dependencies, use userProfile instead

  const totalPropertySlots = Number(planInfo?.total_slots || 0);
const remainingPropertySlots = Math.max(0, totalPropertySlots - properties.length);
const hasAvailableSlots = totalPropertySlots > 0 && properties.length < totalPropertySlots;

  // Get property plan meta - DYNAMICALLY calculates from current data
  // const getPropertyPlanMeta = useCallback((property) => {
  //   const hasFreeTrial = getHasFreeTrial();
  //   const isFirstProperty = properties.length > 0 && property.id === properties[0]?.id;
    
  //   // FREE TRIAL for first property
  //   if (hasFreeTrial && isFirstProperty) {
  //     const userData = userProfile || currentUser;
  //     const createdAt = new Date(userData?.created_at || userData?.createdAt);
  //     const trialEndsAt = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
  //     const now = new Date();
  //     trialEndsAt.setHours(0, 0, 0, 0);
  //     now.setHours(0, 0, 0, 0);
      
  //     const diffMs = trialEndsAt.getTime() - now.getTime();
  //     const daysLeft = diffMs > 0 ? Math.ceil(diffMs / (1000 * 60 * 60 * 24)) : 0;
      
  //     return {
  //       isExpired: daysLeft <= 0,
  //       daysLeft: daysLeft,
  //       label: daysLeft <= 0 ? 'Trial Expired' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left (Trial)`,
  //     };
  //   }
    
  //   // PAID SUBSCRIPTION - Find the subscription assigned to this property
  //   const assignedSubscriptionId = propertySubscriptionIdMap[property.id];
  //   if (assignedSubscriptionId && planInfo.subscriptions) {
  //     const subscription = planInfo.subscriptions.find(sub => sub.id === assignedSubscriptionId);
  //     if (subscription && subscription.ends_at) {
  //       const endDate = new Date(subscription.ends_at);
  //       const now = new Date();
  //       endDate.setHours(0, 0, 0, 0);
  //       now.setHours(0, 0, 0, 0);
        
  //       const diffMs = endDate.getTime() - now.getTime();
  //       const daysLeft = diffMs > 0 ? Math.ceil(diffMs / (1000 * 60 * 60 * 24)) : 0;
        
  //       return {
  //         isExpired: daysLeft <= 0,
  //         daysLeft: daysLeft,
  //         label: daysLeft <= 0 ? 'Expired' : `${daysLeft} day${daysLeft === 1 ? '' : 's'} left`,
  //       };
  //     }
  //   }
    
  //   // Fallback: Check if property has no valid subscription
  //   return {
  //     isExpired: true,
  //     daysLeft: 0,
  //     label: 'Expired',
  //   };
  // }, [properties, userProfile, currentUser, getHasFreeTrial, propertySubscriptionIdMap, planInfo.subscriptions]);
// Get property plan meta - DYNAMICALLY calculates from current data
const getPropertyPlanMeta = useCallback((property) => {
  const hasFreeTrial = getHasFreeTrial();
  const freeListingPropertyId = hasFreeTrial ? getFreeListingPropertyId(properties) : null;
  const isFreeListing = freeListingPropertyId != null && property.id === freeListingPropertyId;

  // FREE TRIAL for first uploaded property
  if (isFreeListing) {
    const userData = userProfile || currentUser;
    const createdAt = new Date(userData?.created_at || userData?.createdAt);
    const trialEndsAt = new Date(createdAt.getTime() + (30 * 24 * 60 * 60 * 1000));
    const now = new Date();
    trialEndsAt.setHours(0, 0, 0, 0);
    now.setHours(0, 0, 0, 0);
    
    const diffMs = trialEndsAt.getTime() - now.getTime();
    const daysLeft = diffMs > 0 ? Math.ceil(diffMs / (1000 * 60 * 60 * 24)) : 0;
    
    return {
      isExpired: daysLeft <= 0,
      daysLeft: daysLeft,
      label: formatDaysLeftLabel(daysLeft, daysLeft <= 0),
      isFree: true,
    };
  }
  
  // PAID SUBSCRIPTION - Find the subscription assigned to this property
  const assignedSubscriptionId = propertySubscriptionIdMap[property.id];
  if (assignedSubscriptionId && planInfo.subscriptions) {
    const subscription = planInfo.subscriptions.find(sub => sub.id === assignedSubscriptionId);
    if (subscription && subscription.ends_at) {
      const endDate = new Date(subscription.ends_at);
      const now = new Date();
      endDate.setHours(0, 0, 0, 0);
      now.setHours(0, 0, 0, 0);
      
      const diffMs = endDate.getTime() - now.getTime();
      const daysLeft = diffMs > 0 ? Math.ceil(diffMs / (1000 * 60 * 60 * 24)) : 0;
      const isExpired = daysLeft <= 0;
      
      // If subscription is expired, clear the mapping so it can be reassigned
      if (isExpired) {
        console.log(`Subscription ${assignedSubscriptionId} for property ${property.id} is expired, clearing mapping`);
        setPropertySubscriptionIdMap(prev => {
          const newMap = { ...prev };
          delete newMap[property.id];
          localStorage.setItem('agentPropertySubscriptionIdMap', JSON.stringify(newMap));
          return newMap;
        });
      }
      
      return {
        isExpired: isExpired,
        daysLeft: daysLeft,
        label: formatDaysLeftLabel(daysLeft, isExpired),
      };
    }
  }
  
  // Fallback: Check if property has no valid subscription
  return {
    isExpired: true,
    daysLeft: 0,
    label: 'Expired',
  };
}, [properties, userProfile, currentUser, getHasFreeTrial, propertySubscriptionIdMap, planInfo.subscriptions]);
  // Handle renew property
  // Replace the handleRenewProperty function with this:

const handleRenewProperty = async (propertyId) => {
  // Store which property is being renewed
  sessionStorage.setItem('renewingPropertyId', propertyId);
  
  // Remove the old mapping for this property
  setPropertySubscriptionIdMap(prev => {
    const newMap = { ...prev };
    delete newMap[propertyId];
    localStorage.setItem('agentPropertySubscriptionIdMap', JSON.stringify(newMap));
    return newMap;
  });
  
  navigate('/agent-dashboard/checkout', {
    state: {
      planId: 'basic_listing',
      renewPropertyId: propertyId,
      returnTo: '/agent-dashboard/properties',
    },
  });
};

  // Post-payment effect to refresh data
 // Post-payment effect to refresh data - IMPROVED VERSION
useEffect(() => {
  const checkPaymentCompletion = async () => {
    const urlParams = new URLSearchParams(window.location.search);
    const paymentSuccess = urlParams.get('payment_success');
    const renewedPropertyId = sessionStorage.getItem('renewingPropertyId');
    
    if (paymentSuccess === 'true' && renewedPropertyId) {
      console.log('Payment successful for property:', renewedPropertyId);
      sessionStorage.removeItem('renewingPropertyId');
      
      // Clear all mappings to force fresh assignment
      setPropertySubscriptionIdMap({});
      localStorage.removeItem('agentPropertySubscriptionIdMap');
      
      // Refresh all data
      await refreshData();
      
      // Wait for data to refresh
      setTimeout(async () => {
        // Fetch fresh subscriptions
        const subscriptions = await fetchSubscriptions();
        
        // Update planInfo with new subscriptions
        setPlanInfo(prev => ({ 
          ...prev, 
          subscriptions: subscriptions,
          total_slots: subscriptions.length + (prev.has_free_trial ? FREE_LISTING_SLOTS : 0)
        }));
        
        // Force a re-render to reassign mappings
        window.dispatchEvent(new Event('storage'));
      }, 3000);
      
      // Remove query param from URL
      window.history.replaceState({}, '', window.location.pathname);
    }
  };
  
  checkPaymentCompletion();
}, [refreshData, fetchSubscriptions]);
// Reassign mappings when subscriptions change (after renewal)
useEffect(() => {
  const reassignMappings = async () => {
    if (properties.length === 0) return;
    if (!planInfo.subscriptions || planInfo.subscriptions.length === 0) return;
    
    const hasFreeTrial = planInfo.has_free_trial;
    
    // Determine which properties need paid subscriptions (exclude free listing slot)
    const freeListingPropertyId = hasFreeTrial ? getFreeListingPropertyId(properties) : null;
    const propertiesNeedingPaid = freeListingPropertyId
      ? properties.filter((p) => p.id !== freeListingPropertyId)
      : properties;
    const newMappings = { ...propertySubscriptionIdMap };
    let changed = false;
    
    propertiesNeedingPaid.forEach((property, index) => {
      // If property doesn't have a subscription assigned, assign one
      if (!newMappings[property.id] && planInfo.subscriptions[index]) {
        newMappings[property.id] = planInfo.subscriptions[index].id;
        changed = true;
        console.log(`Reassigned subscription ${planInfo.subscriptions[index].id} to property ${property.id}`);
      }
    });
    
    if (changed) {
      setPropertySubscriptionIdMap(newMappings);
      localStorage.setItem('agentPropertySubscriptionIdMap', JSON.stringify(newMappings));
    }
  };
  
  reassignMappings();
}, [planInfo.subscriptions, properties, planInfo.has_free_trial]);

  const isPropertyVisibleToBuyers = (property) =>
    property?.isActive === true || property?.isActive === 1 || property?.isActive === '1';

  const handleToggleVisibility = async (property) => {
    try {
      setTogglingPropertyId(property.id);
      const currentlyVisible = isPropertyVisibleToBuyers(property);
      const nextVisible = !currentlyVisible;

      const response = await sellerPropertiesAPI.toggleVisibility(property.id, nextVisible);
      if (response.success !== true) {
        throw new Error(response.message || 'Failed to update property visibility');
      }

      const serverActive = response.data?.property?.is_active;
      const resolvedActive =
        serverActive !== undefined && serverActive !== null
          ? serverActive === true || serverActive === 1 || serverActive === '1'
          : nextVisible;

      setProperties((prev) =>
        prev.map((p) => (p.id === property.id ? { ...p, isActive: resolvedActive } : p))
      );

      if (refreshData) {
        await refreshData();
      }
    } catch (err) {
      console.error('Toggle property visibility failed:', err);
      alert(err.message || 'Failed to update property visibility. Please try again.');
    } finally {
      setTogglingPropertyId(null);
    }
  };

  const handleToggleClick = (property) => {
    setPropertyToToggle(property);
    setVisibilityModalOpen(true);
  };

  const handleVisibilityConfirm = async () => {
    if (!propertyToToggle) return;
    await handleToggleVisibility(propertyToToggle);
    setVisibilityModalOpen(false);
    setPropertyToToggle(null);
  };

  const isUpcomingListing = (p) => String(p?.projectType || '').toLowerCase() === 'upcoming';

  // Search applies to all; For Sale / For Rent filter applies only to regular listings (not the Projects row)
  const searchQuery = (searchTerm || '').trim().toLowerCase();
  const matchesSearch = (p) => {
    if (!searchQuery) return true;
    const title = (p.title || '').toString().toLowerCase();
    const loc = (p.location || '').toString().toLowerCase();
    return title.includes(searchQuery) || loc.includes(searchQuery);
  };

  const sortedByToolbar = [...properties]
    .filter(matchesSearch)
    .sort((a, b) => {
      switch (sortBy) {
        case 'newest':
          return new Date(b.createdAt) - new Date(a.createdAt);
        case 'oldest':
          return new Date(a.createdAt) - new Date(b.createdAt);
        case 'price-high':
          return parseFloat(b.price) - parseFloat(a.price);
        case 'price-low':
          return parseFloat(a.price) - parseFloat(b.price);
        default:
          return 0;
      }
    });

  const normalProperties = sortedByToolbar.filter(
    p => !isUpcomingListing(p) && (filterStatus === 'all' || p.status === filterStatus)
  );
  const upcomingProjects = sortedByToolbar.filter(isUpcomingListing);

  const openNew = () => {
    if (!hasAvailableSlots) {
      alert('You have reached your current plan limit. Purchase another plan to add more properties.');
      navigate('/agent-dashboard/subscription');
      return;
    }
    if (properties.length >= MAX_PROPERTIES) {
      alert(`You can add maximum ${MAX_PROPERTIES} properties.`);
      return;
    }
    setEditIndex(null);
    setEditProperty(null);
    setShowForm(true);
    setShowUpcomingProjectPopup(false);
  };

  const handleAddUpcomingProject = () => {
    if (!hasAvailableSlots) {
      alert('You have reached your current plan limit. Purchase another plan to add more properties.');
      navigate('/agent-dashboard/subscription');
      return;
    }
    if (properties.length >= MAX_PROPERTIES) {
      alert(`You can add maximum ${MAX_PROPERTIES} properties.`);
      return;
    }
    setEditProperty(null);
    setShowUpcomingProjectPopup(true);
    setShowForm(false);
  };

  const openEdit = (idx) => {
    const property = properties[idx];
    if (!property) return;

    setEditProperty(property);

    if (isUpcomingListing(property)) {
      setEditIndex(null);
      setShowUpcomingProjectPopup(true);
      setShowForm(false);
    } else {
      setEditIndex(idx);
      setShowForm(true);
      setShowUpcomingProjectPopup(false);
    }
  };

  const handleDeleteClick = (id) => {
    setPropertyToDelete(id);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!propertyToDelete) return;
    try {
      setDeletingId(propertyToDelete);
      await deleteProperty(propertyToDelete);
      
      // Remove mapping for deleted property
      setPropertySubscriptionIdMap(prev => {
        const newMap = { ...prev };
        delete newMap[propertyToDelete];
        localStorage.setItem('agentPropertySubscriptionIdMap', JSON.stringify(newMap));
        return newMap;
      });
      
      setPropertyToDelete(null);
    } catch (error) {
      console.error('Error deleting property:', error);
      alert(error.message || 'Failed to delete property. Please try again.');
    } finally {
      setDeletingId(null);
    }
  };

  const handleProjectStatusChange = async (propertyId, newStatus) => {
    const property = properties.find(p => p.id === propertyId);
    if (!property || !isUpcomingListing(property)) return;
    let upcomingData = property.upcomingProjectData;
    if (typeof upcomingData === 'string') {
      try {
        upcomingData = JSON.parse(upcomingData);
      } catch {
        upcomingData = {};
      }
    }
    if (!upcomingData || typeof upcomingData !== 'object') {
      upcomingData = {};
    }
    const mergedData = { ...upcomingData, projectStatus: newStatus };
    try {
      setUpdatingStatusId(propertyId);
      const response = await sellerPropertiesAPI.update(propertyId, {
        upcoming_project_data: mergedData
      });
      if (response.success && refreshData) {
        await refreshData();
      } else {
        throw new Error(response.message || 'Failed to update status');
      }
    } catch (error) {
      console.error('Error updating project status:', error);
      alert(error.message || 'Failed to update project status. Please try again.');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const getUpcomingProjectStatus = (property) => {
    const data = property.upcomingProjectData;
    if (!data) return 'UNDER CONSTRUCTION';
    const parsed = typeof data === 'string' ? (() => { try { return JSON.parse(data); } catch { return {}; } })() : data;
    return parsed.projectStatus || 'UNDER CONSTRUCTION';
  };

  const formatPrice = (price) => {
    const num = parseFloat(price);
    if (num >= 10000000) {
      return `₹${(num / 10000000).toFixed(2)} Cr`;
    } else if (num >= 100000) {
      return `₹${(num / 100000).toFixed(2)} Lac`;
    } else if (num >= 1000) {
      return `₹${(num / 1000).toFixed(1)}K`;
    }
    return `₹${num}`;
  };

  const getPropertyIndex = (propertyId) => {
    return properties.findIndex(p => p.id === propertyId);
  };

  const handleViewDetails = (propertyId) => {
    const property = properties.find(p => p.id === propertyId);
    if (property && isUpcomingListing(property)) {
      window.open(`/agent-dashboard/upcoming-project/${propertyId}`, '_blank', 'noopener,noreferrer');
    } else {
      window.open(`/agent-dashboard/details/${propertyId}`, '_blank', 'noopener,noreferrer');
    }
  };

  const renderPropertyCarouselCard = (property, index, { isUpcomingCard = false } = {}) => {
    const planMeta = getPropertyPlanMeta(property);
    const isActive = !!property.isActive;
    const showProjectStatusSelect = isUpcomingCard || isUpcomingListing(property);

    return (
      <div
        key={property.id}
        className="seller-props-card grid"
        style={{ animationDelay: `${index * 0.05}s`, cursor: 'pointer', flexShrink: 0 }}
        onClick={() => handleViewDetails(property.id)}
      >
        <div className="seller-props-image">
          <img
            src={(() => {
              const imageUrl = property.display_image || property.images?.[0] || property.cover_image;
              if (!imageUrl) return 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=500';
              if (imageUrl.startsWith('http://') || imageUrl.startsWith('https://')) {
                return imageUrl;
              }
              if (imageUrl.startsWith('/')) {
                return `${API_BASE_URL.replace('/api', '')}${imageUrl}`;
              }
              return `${API_BASE_URL.replace('/api', '')}/uploads/${imageUrl}`;
            })()}
            alt={property.title || 'Listing'}
            onError={(e) => {
              console.error('Image load failed for property:', property.id, 'URL:', e.target.src);
              e.target.src = 'https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?w=500';
            }}
          />
          <div className="seller-props-image-overlay">
            <button
              type="button"
              className="seller-props-overlay-btn"
              onClick={(e) => {
                e.stopPropagation();
                openEdit(getPropertyIndex(property.id));
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" />
                <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" />
              </svg>
            </button>
            <button
              type="button"
              className="seller-props-overlay-btn delete"
              onClick={(e) => {
                e.stopPropagation();
                handleDeleteClick(property.id);
              }}
              disabled={deletingId === property.id}
              title={deletingId === property.id ? 'Deleting...' : 'Delete property'}
            >
              {deletingId === property.id ? (
                <div style={{
                  width: '16px',
                  height: '16px',
                  border: '2px solid #fff',
                  borderTop: '2px solid transparent',
                  borderRadius: '50%',
                  animation: 'spin 0.6s linear infinite',
                  display: 'inline-block'
                }}></div>
              ) : (
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="currentColor" strokeWidth="2" />
                </svg>
              )}
            </button>
          </div>
          <span className={`seller-props-badge ${isUpcomingCard ? 'sale' : property.status}`}>
            {isUpcomingCard ? 'Upcoming Project' : (property.status === 'sale' ? 'For Sale' : 'For Rent')}
          </span>
          <span className={`seller-props-plan-days ${planMeta.isExpired ? 'expired' : 'active'}`}>
            {planMeta.daysLeft != null
              ? formatDaysLeftLabel(planMeta.daysLeft, planMeta.isExpired)
              : planMeta.label}
          </span>
          {showProjectStatusSelect && (
            <select
              className="seller-props-project-status-select"
              value={getUpcomingProjectStatus(property)}
              onChange={(e) => {
                e.stopPropagation();
                handleProjectStatusChange(property.id, e.target.value);
              }}
              onClick={(e) => e.stopPropagation()}
              disabled={updatingStatusId === property.id}
              title="Change project status"
            >
              {PROJECT_STATUS_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>{opt.label}</option>
              ))}
            </select>
          )}
          {property.featured && (
            <span className="seller-props-featured-badge">Featured</span>
          )}
        </div>

        <div className="seller-props-content">
          <div className="seller-props-title-row">
            <h3 className="seller-props-title">{property.title}</h3>
            <span className={`seller-props-live-status ${isActive ? 'on' : 'off'}`}>
              {isActive ? 'On' : 'Off'}
            </span>
          </div>
          {planMeta.isFree && !planMeta.isExpired && (
            <p className="seller-props-free-plan">{FREE_PLAN_LABEL}</p>
          )}
          <p className="seller-props-location">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
              <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0118 0z" stroke="currentColor" strokeWidth="2" />
              <circle cx="12" cy="10" r="3" stroke="currentColor" strokeWidth="2" />
            </svg>
            {property.location || '—'}
          </p>

          <div className="seller-props-features">
            {!showProjectStatusSelect && (
              <>
                <span className="seller-props-feature">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 22V8l9-6 9 6v14H3z" stroke="currentColor" strokeWidth="2" />
                    <path d="M9 22v-6h6v6" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  {property.bedrooms ?? '—'} Beds
                </span>
                <span className="seller-props-feature">
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M4 12h16v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5z" stroke="currentColor" strokeWidth="2" />
                    <path d="M6 12V5a2 2 0 012-2h2v9" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  {property.bathrooms ?? '—'} Baths
                </span>
              </>
            )}
            {property.area != null && property.area !== '' && (
              <span className="seller-props-feature">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                  <rect x="3" y="3" width="18" height="18" rx="2" stroke="currentColor" strokeWidth="2" />
                  <path d="M3 9h18M9 21V9" stroke="currentColor" strokeWidth="2" />
                </svg>
                {property.area} sqft
              </span>
            )}
          </div>

          <div className="seller-props-stats">
            <div className="seller-props-stat-item" title={`${property.views || 0} people showed interest in this property`}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" />
                <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
              </svg>
              <span>{property.views || 0} {property.views === 1 ? 'person' : 'people'} interested</span>
            </div>
            <div className="seller-props-stat-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z" stroke="currentColor" strokeWidth="2" />
              </svg>
              <span>{property.inquiries || 0} inquiries</span>
            </div>
          </div>

          <div className="seller-props-footer">
            <div className="seller-props-footer-top">
              <div className="seller-props-price-section">
                <span className="seller-props-price">{formatPrice(property.price)}</span>
                {property.status === 'rent' && <span className="seller-props-per-month">/month</span>}
              </div>
            </div>
            <div className="seller-props-action-btns">
              <div className="seller-props-action-group seller-props-action-group-left">
                <button
                  type="button"
                  className="seller-props-edit-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    openEdit(getPropertyIndex(property.id));
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" stroke="currentColor" strokeWidth="2" />
                    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  Edit
                </button>
                <button
                  type="button"
                  className="seller-props-view-details-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewDetails(property.id);
                  }}
                  title="View Details"
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" stroke="currentColor" strokeWidth="2" />
                    <circle cx="12" cy="12" r="3" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  View
                </button>
              </div>
              <div className="seller-props-action-group seller-props-action-group-right">
                <button
                  type="button"
                  className={`seller-props-toggle-btn ${isActive ? 'on' : 'off'}`}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleToggleClick(property);
                  }}
                  disabled={togglingPropertyId === property.id}
                  title={
                    isActive
                      ? 'Hide this property from buyer search and details'
                      : 'Show this property to buyers again'
                  }
                >
                  {togglingPropertyId === property.id
                    ? 'Updating...'
                    : isActive
                      ? 'Hide'
                      : 'Show'}
                </button>
                <button
                  type="button"
                  className="seller-props-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteClick(property.id);
                  }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" stroke="currentColor" strokeWidth="2" />
                  </svg>
                  Delete
                </button>
                {planMeta.isExpired && (
                  <button
                    type="button"
                    className="seller-props-renew-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRenewProperty(property.id);
                    }}
                  >
                    Renew
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="seller-properties agent-properties-page">
      {/* Header */}
      <div className="seller-props-header">
        <div className="seller-props-header-content">
          <div>
            <h1>My Properties</h1>
            <p className="seller-props-subtitle">
              {loading ? 'Loading...' : `${properties.length} listing${properties.length === 1 ? '' : 's'} (properties + projects)`}
            </p>
            <p className="seller-props-plan-status">
              {isPlanLoading
                ? 'Checking plan slots...'
                : hasAvailableSlots
                  ? `${remainingPropertySlots} property slot${remainingPropertySlots === 1 ? '' : 's'} available`
                  : totalPropertySlots > 0
                    ? `No property slot available. ${properties.length}/${totalPropertySlots} slots used.`
                    : 'No property slot available. Purchase a plan to add more.'}
            </p>
          </div>
          <div className="agent-seller-props-header-buttons">
            <button className="agent-seller-props-add-btn" onClick={openNew}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              Add Property
            </button>
            <button className="agent-seller-props-add-btn" onClick={handleAddUpcomingProject}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
              <span>Add New project</span>
            </button>
          </div>
        </div>
      </div>

      {/* Toolbar */}
      <div className="seller-props-toolbar">
        <div className="seller-props-search-box">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
            <circle cx="11" cy="11" r="8" stroke="currentColor" strokeWidth="2" />
            <path d="M21 21l-4.35-4.35" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
          </svg>
          <input
            type="text"
            placeholder="Search properties and projects..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="seller-props-toolbar-actions">
          <div className="seller-props-filter-group">
            <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="all">All Status</option>
              <option value="sale">For Sale</option>
              <option value="rent">For Rent</option>
            </select>

            <select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="newest">Newest First</option>
              <option value="oldest">Oldest First</option>
              <option value="price-high">Price: High to Low</option>
              <option value="price-low">Price: Low to High</option>
            </select>
          </div>
        </div>
      </div>

      {/* Properties Section */}
      <div className="agent-props-sections">
        {/* Section 1: Normal Properties */}
        <div className="agent-props-section">
          <h2 className="agent-props-section-heading">Properties</h2>
          {normalProperties.length === 0 ? (
            <div className="agent-props-empty-message">
              <p>No properties found</p>
            </div>
          ) : (
            <div className="agent-props-horizontal-scroll">
              {normalProperties.map((property, index) =>
                renderPropertyCarouselCard(property, index, { isUpcomingCard: false })
              )}

              <div
                className="seller-props-card seller-props-add-card"
                onClick={openNew}
                style={{ flexShrink: 0 }}
              >
                <div className="seller-props-add-card-content">
                  <div className="seller-props-add-icon">
                    <svg width="32" height="32" viewBox="0 0 24 24" fill="none">
                      <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                    </svg>
                  </div>
                  <h3>Add New Property</h3>
                  <p>List a new property for sale or rent</p>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Section 2: Upcoming Projects */}
        <div className="agent-props-section">
          <h2 className="agent-props-section-heading">Projects</h2>
          {upcomingProjects.length === 0 ? (
            <div className="agent-props-empty-message">
              <p>No upcoming projects found</p>
            </div>
          ) : (
            <div className="agent-props-horizontal-scroll">
              {upcomingProjects.map((property, index) =>
                renderPropertyCarouselCard(property, index, { isUpcomingCard: true })
              )}
            </div>
          )}
        </div>
      </div>

      {/* Add/Edit Form - Normal Property */}
      {showForm && (
        <AddPropertyPopup
          onClose={() => {
            setShowForm(false);
            setEditIndex(null);
            setEditProperty(null);
          }}
          editIndex={editIndex}
          initialData={editProperty && !isUpcomingListing(editProperty) ? editProperty : null}
        />
      )}

      {/* Add/Edit Form - Upcoming Project */}
      {showUpcomingProjectPopup && (
        <AddUpcomingProjectPopup
          onClose={() => {
            setShowUpcomingProjectPopup(false);
            setEditProperty(null);
          }}
          editData={editProperty}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeletePropertyModal
        isOpen={deleteModalOpen}
        onClose={() => {
          setDeleteModalOpen(false);
          setPropertyToDelete(null);
        }}
        onConfirm={handleDeleteConfirm}
      />

      {visibilityModalOpen && propertyToToggle && (
        <div
          className="seller-props-visibility-modal-backdrop"
          onClick={() => {
            setVisibilityModalOpen(false);
            setPropertyToToggle(null);
          }}
        >
          <div className="seller-props-visibility-modal" onClick={(e) => e.stopPropagation()}>
            <h3>{propertyToToggle.isActive ? 'Hide Property?' : 'Show Property?'}</h3>
            <p>
              {propertyToToggle.isActive
                ? 'This property will be hidden from buyers until you turn it on again.'
                : 'This property will be visible to buyers after you turn it on.'}
            </p>
            <div className="seller-props-visibility-modal-actions">
              <button
                type="button"
                className="seller-props-visibility-cancel"
                onClick={() => {
                  setVisibilityModalOpen(false);
                  setPropertyToToggle(null);
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                className="seller-props-visibility-confirm"
                onClick={handleVisibilityConfirm}
                disabled={togglingPropertyId === propertyToToggle.id}
              >
                {togglingPropertyId === propertyToToggle.id
                  ? 'Updating...'
                  : propertyToToggle.isActive
                    ? 'Hide Property'
                    : 'Show Property'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AgentProperties;